<template>
  <div
    v-click-outside="closeDropdown"
    class="v2-reps-filter"
  >
    <button
      class="v2-reps-filter__toggle"
      @click="isOpen = !isOpen"
    >
      <span>{{ placeholder }} {{ selectedNames.length ? `(${selectedNames.length})` : '' }}</span>
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        :class="{ 'v2-reps-filter__chevron--open': isOpen }"
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </button>
    <div
      v-if="isOpen"
      class="v2-reps-filter__dropdown"
    >
      <div class="v2-reps-filter__search">
        <v2-input
          placeholder="Search"
          :value="searchText"
          @input="onSearchSalesRep"
        />
      </div>
      <div
        v-if="!filteredSalesReps.length"
        class="v2-reps-filter__empty"
      >
        Nothing was found. Try another name.
      </div>
      <div
        v-else
        class="v2-reps-filter__options"
      >
        <v2-checkbox
          v-for="option in filteredSalesReps"
          :key="option.userId"
          :value="selectedRepIds.includes(option.userId)"
          :label="option.name"
          @input="toggleOption(option)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import V2Input from '@/components/v2/V2Input.vue'
import V2Checkbox from '@/components/v2/V2Checkbox.vue'
import { getSelfOption } from '@/service/user.service'

export default {
  name: 'V2RepsFilterDropdown',
  components: {
    V2Input,
    V2Checkbox,
  },
  directives: {
    'click-outside': {
      bind(el, binding) {
        el._clickOutside = e => {
          if (!el.contains(e.target)) {
            binding.value()
          }
        }
        document.addEventListener('click', el._clickOutside)
      },
      unbind(el) {
        document.removeEventListener('click', el._clickOutside)
      },
    },
  },
  props: {
    options: {
      type: Array,
      default: () => [],
    },
    showSelfOption: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: 'Sales representative',
    },
  },
  data() {
    return {
      isOpen: false,
      selectedRepIds: [],
      selectedNames: [],
      filteredSalesReps: [],
      searchText: '',
    }
  },
  watch: {
    showSelfOption() {
      this.setOptions()
    },
    options: {
      handler() {
        this.setOptions()
      },
      deep: true,
    },
  },
  created() {
    this.setOptions()
  },
  methods: {
    setOptions() {
      if (this.showSelfOption) {
        this.filteredSalesReps = [getSelfOption(), ...this.options]
      } else {
        this.filteredSalesReps = this.options
      }
    },
    onSearchSalesRep(searchValue = '') {
      this.searchText = searchValue
      if (!searchValue) {
        this.setOptions()
        return
      }
      const allOptions = this.showSelfOption ? [getSelfOption(), ...this.options] : this.options
      this.filteredSalesReps = allOptions.filter(
        s => s.name.toLowerCase().includes(searchValue.toLowerCase()),
      )
    },
    toggleOption(option) {
      const idx = this.selectedRepIds.indexOf(option.userId)
      if (idx !== -1) {
        this.selectedRepIds.splice(idx, 1)
      } else {
        this.selectedRepIds.push(option.userId)
      }
      const allOptions = this.showSelfOption ? [getSelfOption(), ...this.options] : this.options
      const selectedValues = allOptions.filter(s => this.selectedRepIds.includes(s.userId))
      this.selectedNames = selectedValues.map(s => s.name)
      this.$emit('on-select-options', selectedValues)
    },
    closeDropdown() {
      this.isOpen = false
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-reps-filter {
  position: relative;
  min-width: 225px;

  &__toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 38px;
    padding: var(--v2-space-2) var(--v2-space-3);
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-foreground);
    // Glass trigger — matches V2Input / V2Dropdown / V2Select recipe so the
    // Sales rep / Sales manager filters blend with the rest of the v2 form
    // controls in dense filter rows (V2OrderList top filter bar).
    background: var(--v2-glass-surface-2);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-md);
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover {
      border-color: var(--v2-glass-stroke-strong);
    }
  }

  &__chevron--open {
    transform: rotate(180deg);
  }

  &__dropdown {
    position: absolute;
    top: calc(100% + var(--v2-space-1));
    left: 0;
    width: 100%;
    // Floating glass — uses --v2-glass-surface-floating because this menu
    // can render over arbitrary content; matches V2Select / V2Dropdown.
    background: var(--v2-glass-surface-floating);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-md);
    box-shadow: var(--v2-glass-shadow-floating);
    z-index: 100;
    max-height: 300px;
    overflow-y: auto;
  }

  &__search {
    padding: var(--v2-space-2) var(--v2-space-3);
    border-bottom: 1px solid var(--v2-border);
  }

  &__empty {
    padding: var(--v2-space-4);
    text-align: center;
    font-size: 0.875rem;
    color: var(--v2-muted-foreground);
  }

  &__options {
    padding: var(--v2-space-2) var(--v2-space-3);
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-3);
  }
}
</style>
